import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { UserAssetsHttpService } from '@core/http/user-assets.http.service';
import * as actions from './user-files.actions';
import { FileHandlerService } from '@core/services/file-handler.service';

@Injectable()
export class UserFilesEffects {
  private readonly actions$ = inject(Actions);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly userAssetsService = inject(UserAssetsHttpService);
  private readonly fileHandlerService = inject(FileHandlerService);

  getFilesOfUserDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getFilesOfUserDocuments),
      switchMap(({ opts }) => {
        return this.userAssetsService.getUserFilesOfDocuments(opts).pipe(
          map(filesOfDocuments => {
            return actions.getFilesOfUserDocumentsSuccess({ filesOfDocuments, getOpts: opts });
          }),
          catchError(() => {
            this.stackedMessages.showError({ message: 'NT3.ERROR_DOWNLOADING_FILES_LIST' });
            return of(actions.getFilesOfUserDocumentsError());
          })
        );
      })
    )
  );

  getUserAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getUserAssets),
      switchMap(({ opts }) => {
        return this.userAssetsService.getUserAssets(opts).pipe(
          map(userAssets => {
            return actions.getUserAssetsSuccess({ userAssets, getOpts: opts });
          }),
          catchError(() => {
            this.stackedMessages.showError({ message: 'NT3.ERROR_DOWNLOADING_FILES_LIST' });
            return of(actions.getUserAssetsError());
          })
        );
      })
    )
  );

  downloadUserAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.downloadAsset),
      mergeMap(({ userAssetId, previewTargetKey }) => {
        return this.userAssetsService.downloadAnyAsset({ userAssetId }).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return actions.downloadAssetSuccess();
            } else {
              return actions.downloadAssetError();
            }
          }),
          catchError(() => {
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            this.stackedMessages.showError({ message: 'COMMON.ERROR.DOWNLOADING_ATTACHMENT' });
            return of(actions.downloadAssetError());
          })
        );
      })
    )
  );
}
